import { EavDataTypes } from './eav-data-types.enum';
import { FilterOperations } from './filter-operation/filter-operations.enum';
import { FilterPrimitive } from './filter-primitives';
import { LogicalOperators } from './logical-operators.enum';

export interface FilterExpression {
   filterOptions: FilterOption[];
   logicalOperator?: LogicalOperators;
   subExpressions?: FilterExpression[] | null;
}

export type FilterDefinition = Omit<FilterExpression, 'logicalOperator' | 'subExpressions'> & {
   filterId: number | null;
   name: string;
   isDirty?: boolean;
   displayName?: string;
};

export interface CustomFieldFilterOptionConfig {
   customFieldDefinitionId?: number;
   customFieldDefinitionName?: string;
   customFieldDataType?: EavDataTypes;
}

export interface FilterOption extends CustomFieldFilterOptionConfig {
   basePath?: string;
   useAllForNotIn?: boolean;
   field: string;
   operation: FilterOperations;
   value: FilterPrimitive | FilterPrimitive[] | null;
}

export function buildPrimaryKeyFilterExpression<TItem>(
   primaryKey: keyof TItem & string,
   existingItems: TItem[] | null
): FilterExpression | null;
export function buildPrimaryKeyFilterExpression<TItem, TApiResponseItem>(
   primaryKey: keyof TApiResponseItem & string,
   existingItems: TItem[] | null,
   getExistingPrimaryKey?: (item: TItem) => FilterPrimitive
): FilterExpression | null;
export function buildPrimaryKeyFilterExpression<TItem>(
   primaryKey: string,
   existingItems: TItem[] | null,
   getExistingPrimaryKey?: (item: TItem) => FilterPrimitive
): FilterExpression | null {
   if (existingItems && existingItems.length > 0) {
      return {
         filterOptions: [
            {
               field: primaryKey,
               operation: FilterOperations.NotIn,
               value: existingItems.map(
                  getExistingPrimaryKey
                     ? getExistingPrimaryKey
                     : (item): any => {
                          return item[primaryKey];
                       }
               ),
            },
         ],
      };
   }
   return null;
}

export function serializeFilterExpression(filterExpression: null): null;
export function serializeFilterExpression(filterExpression: FilterExpression): string;
export function serializeFilterExpression(filterExpression: FilterExpression | null): string | null;
export function serializeFilterExpression(filterExpression: FilterExpression | null): string | null {
   if (!filterExpression) {
      return null;
   }

   const logicalOperator = filterExpression.logicalOperator || LogicalOperators.And;

   let query: string = serializeOptions(filterExpression.filterOptions, logicalOperator);

   filterExpression.subExpressions?.forEach((subExpression) => {
      const filterExpressionString = serializeFilterExpression(subExpression);
      if (filterExpressionString.length > 0) {
         // If any filter expression has no filter options, skip over it
         // e.g., { filterOptions: [], subExpressions: [{ field: "Field", operation: EqualTo, value: 5 }] }
         // should be serialized to "Field = 5", not " And Field = 5"
         if (query.length === 0) {
            query = filterExpressionString;
         } else {
            query = `${query} ${logicalOperator} (${filterExpressionString})`;
         }
      }
   });

   return query;
}

export function serializeMultiFieldSubExpressions(filterExpression: FilterExpression | null): string | null {
   if (!filterExpression) {
      return null;
   }

   const logicalOperator = filterExpression.logicalOperator || LogicalOperators.And;

   let query: string = serializeOptions(filterExpression.filterOptions, logicalOperator);

   if (filterExpression.subExpressions && filterExpression.subExpressions.length) {
      const subQueries = filterExpression.subExpressions
         .map((subExpression) => {
            const serialized = serializeFilterExpression(subExpression);
            return serialized ? `(${serialized})` : null;
         })
         .filter(Boolean) as string[];

      const combinedSubQueries = subQueries.join(` ${LogicalOperators.And} `);

      if (query.length > 0) {
         query = `${query} ${logicalOperator} ${combinedSubQueries}`;
      } else {
         query = combinedSubQueries;
      }
   }

   return query;
}

export function filterDefinitionToString(filter: FilterDefinition): string {
   return serializeOptions(filter.filterOptions, LogicalOperators.And);
}

interface FilterOptionBase {
   basePath?: string;
   field: string;
   operation: FilterOperations;
   useAllForNotIn?: boolean;
}

export function serializeFilterOption<TFilterOption extends FilterOptionBase>(
   option: TFilterOption,
   value: FilterPrimitive | FilterPrimitive[]
): string {
   const operation: string = getFilterOperationApiString(option.operation);
   let stringValue: string;
   const path: string = FilterOperations.HasValue === option.operation ? option.basePath ?? option.field : option.field;
   if (option.operation === FilterOperations.Between) {
      stringValue = serializeBetweenFilterValue(value);
   } else if (option.operation === FilterOperations.HasValue) {
      stringValue = serializeHasValueFilterValue(value);
   } else if (option.operation === FilterOperations.NotIn && option.useAllForNotIn && option.basePath) {
      return `((All ${path} ${operation} ${serializeFilterValue(value)}) or ${option.basePath} is null)`;
   } else {
      stringValue = serializeFilterValue(value);
   }

   if (
      option.operation === FilterOperations.GreaterThanOrEqualToOrNull ||
      option.operation === FilterOperations.EqualToOrNull ||
      option.operation === FilterOperations.GreaterThanOrNull ||
      option.operation === FilterOperations.LessThanOrEqualToOrNull ||
      option.operation === FilterOperations.LessThanOrNull
   ) {
      return serializeNullOperatorFilterOption(option.field, operation, stringValue);
   }

   return `${path} ${operation} ${stringValue}`;
}

function serializeOptions(filterOptions: FilterOption[], operator: LogicalOperators): string {
   return filterOptions
      .filter((fo) => {
         return fo.value != null;
      })
      .map((option) => {
         return serializeFilterOption(option, option.value!);
      })
      .join(` ${operator} `);
}

/**
 * Serializes the value of a filter option using JSON.stringify
 * @param value The value of a filter option
 */
export function serializeFilterValue(value: FilterPrimitive | FilterPrimitive[] | null): string {
   if (value == null) {
      throw new Error('Invalid value for filter. Value cannot be null or undefined.');
   }
   if (Array.isArray(value)) {
      return `(${value.map((v) => {
         return JSON.stringify(v);
      })})`;
   } else {
      return JSON.stringify(value);
   }
}

export function serializeBetweenFilterValue(value: FilterPrimitive | FilterPrimitive[] | null): string {
   if (!Array.isArray(value)) {
      throw new Error('Invalid value for Between filter. Value must be an array.');
   }
   if (value.length !== 2 || value[0] == null || value[1] == null) {
      throw new Error('Invalid value for Between filter. Value must be an array of 2 non-null values.');
   }
   return `${JSON.stringify(value[0])} and ${JSON.stringify(value[1])}`;
}

export function serializeHasValueFilterValue(value: FilterPrimitive | FilterPrimitive[] | null): string {
   if (value == null) {
      throw new Error('Invalid value for filter. Value cannot be null or undefined.');
   }
   if (Array.isArray(value)) {
      throw new Error('Invalid value for Has Value filter. Value cannot be an array.');
   }
   if (value === false) {
      return 'null';
   } else if (value === true) {
      return 'not null';
   } else {
      throw new Error('Invalid value for Has Value filter. Value must be true or false');
   }
}

function serializeNullOperatorFilterOption(field: string, operator: string, value: string): string {
   return `(${field} ${operator} ${value} or ${field} is null)`;
}

// The null part of the 'or null' operators is handled by the serializeNullOperatorFilterOption
function getFilterOperationApiString(operation: FilterOperations): string {
   switch (operation) {
      case FilterOperations.LessThan:
      case FilterOperations.LessThanOrNull:
         return '<';
      case FilterOperations.LessThanOrEqualTo:
      case FilterOperations.LessThanOrEqualToOrNull:
         return '<=';
      case FilterOperations.GreaterThan:
      case FilterOperations.GreaterThanOrNull:
         return '>';
      case FilterOperations.GreaterThanOrEqualTo:
      case FilterOperations.GreaterThanOrEqualToOrNull:
         return '>=';
      case FilterOperations.EqualToOrNull:
         return '=';
      case FilterOperations.NotEqualTo:
         return '!=';
      case FilterOperations.EqualTo:
         return '=';
      case FilterOperations.In:
         return 'in';
      case FilterOperations.NotIn:
         return 'not in';
      case FilterOperations.Between:
         return 'between';
      case FilterOperations.Contains:
         return 'contains';
      case FilterOperations.StartsWith:
         return 'starts with';
      case FilterOperations.EndsWith:
         return 'ends with';
      case FilterOperations.HasValue:
         return 'is';
      default:
         throw new Error(`Invalid filter operation '${operation}'`);
   }
}
export interface FilterExecutionDefinition extends FilterDefinition {
   filterOptions: FilterExecutionOption[];
   filterId: number | null;
}

export interface FilterExecutionOption extends FilterOption {
   field: string;
   values: FilterPrimitive[] | null;
}

export function toFilterExecutionDefinition(filterDefinition: FilterDefinition | null): string | undefined {
   if (filterDefinition && filterDefinition.isDirty && filterDefinition.filterOptions) {
      return filterDefinitionToString(filterDefinition);
   } else {
      return undefined;
   }
}
